import { useState, memo, useMemo, useCallback } from 'react';

interface ProductImageProps {
  src?: string;
  alt?: string;
  className?: string;
  width?: number;
  height?: number;
  /** Set to true for above-the-fold images to improve LCP */
  priority?: boolean;
  /** Sizes attribute for responsive images */
  sizes?: string;
  /** Show skeleton placeholder while loading (default: true) */
  showSkeleton?: boolean;
  /** Callback when image loads */
  onLoad?: () => void;
}

const PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTYwQzE4OC45NTQgMTYwIDE4MCAxNjguOTU0IDE4MCAxODBWMjIwQzE4MCAyMzEuMDQ2IDE4OC45NTQgMjQwIDIwMCAyNDBIMjIwQzIzMS4wNDYgMjQwIDI0MCAyMzEuMDQ2IDI0MCAyMjBWMTgwQzI0MCAxNjguOTU0IDIzMS4wNDYgMTYwIDIyMCAxNjBIMjAwWiIgZmlsbD0iI0QxRDVEQiIvPgo8L3N2Zz4=';

// Srcset widths for responsive images
const SRCSET_WIDTHS = [200, 400, 600, 800, 1000, 1200];

export const ProductImage = memo(function ProductImage({
  src,
  alt = '',
  className = '',
  width,
  height,
  priority = false,
  sizes,
  showSkeleton = true,
  onLoad: onLoadCallback,
}: ProductImageProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const imageSrc = error || !src ? PLACEHOLDER : src;

  // Memoized optimized src
  const optimizedSrc = useMemo(() => optimizeShopifyImage(imageSrc, width, height), [imageSrc, width, height]);
  
  // Generate srcset for Shopify images
  const srcSet = useMemo(() => {
    if (!src || !src.includes('cdn.shopify.com')) return undefined;
    return SRCSET_WIDTHS
      .map(w => `${optimizeShopifyImage(src, w)} ${w}w`)
      .join(', ');
  }, [src]);
  
  // Memoized handlers
  const handleLoad = useCallback(() => {
    setLoaded(true);
    onLoadCallback?.();
  }, [onLoadCallback]);
  const handleError = useCallback(() => setError(true), []);

  // Build image props
  const imgProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    src: optimizedSrc,
    alt,
    className: `${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`,
    loading: priority ? 'eager' : 'lazy',
    decoding: priority ? 'sync' : 'async',
    onLoad: handleLoad,
    onError: handleError,
    width,
    height,
    sizes,
    srcSet,
  };

  // Add fetchpriority for priority images (lowercase for HTML attribute)
  if (priority) {
    (imgProps as any).fetchpriority = 'high';
  }

  // Show skeleton placeholder while loading
  if (showSkeleton && !loaded && !error) {
    return (
      <div className={`relative ${className}`}>
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse" />
        <img {...imgProps} />
      </div>
    );
  }

  return <img {...imgProps} />;
});

function optimizeShopifyImage(
  url: string,
  width?: number,
  height?: number
): string {
  if (!url.includes('cdn.shopify.com')) {
    return url;
  }

  // Shopify image URL optimization
  const sizeParam = width ? `_${width}x${height || width}` : '';

  // Insert size before file extension
  return url.replace(/(\.\w+)(\?.*)?$/, `${sizeParam}$1$2`);
}

